import { useQuery } from "@tanstack/react-query";
import { getWorkerAttendanceProfile } from "@src/containers/attendanceScore/api";
import { WorkerAttendanceProfile } from "@src/containers/attendanceScore/types";

interface UseWorkerAttendanceProfileOptions {
  workerId?: string;
  enabled?: boolean;
  onSuccess?: (data: WorkerAttendanceProfile) => void;
  onError?: (error: Error) => void;
  withPerformanceSince?: string;
}

export function useWorkerAttendanceProfile(options: UseWorkerAttendanceProfileOptions) {
  const { workerId, enabled = true, onSuccess, onError, withPerformanceSince } = options;

  const queryEnabled = Boolean(workerId && enabled);

  return useQuery<WorkerAttendanceProfile, Error>({
    queryKey: ["workerAttendanceProfile", workerId, withPerformanceSince],
    queryFn: async () => {
      if (!workerId) {
        throw new Error("Worker ID is required");
      }
      return getWorkerAttendanceProfile(workerId, withPerformanceSince);
    },
    enabled: queryEnabled,
    onSuccess,
    onError,
    refetchOnWindowFocus: false,
    staleTime: 10000,
  });
}

// Helper functions for attendance profile analysis
export function hasHighCallOff(
  attendanceProfile: WorkerAttendanceProfile | undefined,
  maxAllowedCallOffs?: number,
  maxAllowedPercentageOfCallOffs?: number
): boolean {
  if (!attendanceProfile || !maxAllowedCallOffs || !maxAllowedPercentageOfCallOffs) {
    return false;
  }

  const totalNCNS = attendanceProfile?.performanceMetrics?.totalNCNS ?? 0;
  const shortNoticeCancellations =
    attendanceProfile?.performanceMetrics?.shortNoticeCancellations ?? 0;

  // Want to avoid dividing by 0
  const completedShifts = Math.max(attendanceProfile?.performanceMetrics?.completedShifts ?? 0, 1);

  const totalCallOffs = Math.max(totalNCNS, shortNoticeCancellations);

  return (
    totalCallOffs >= maxAllowedCallOffs ||
    totalCallOffs / completedShifts >= maxAllowedPercentageOfCallOffs
  );
}

export function getTotalCallOffs(attendanceProfile: WorkerAttendanceProfile | undefined): number {
  if (!attendanceProfile) {
    return 0;
  }

  const totalNCNS = attendanceProfile?.performanceMetrics?.totalNCNS ?? 0;
  const shortNoticeCancellations =
    attendanceProfile?.performanceMetrics?.shortNoticeCancellations ?? 0;

  return Math.max(totalNCNS, shortNoticeCancellations);
}
